import Link from 'next/link';
import Head from 'next/head';
import Image from 'next/image';
import type { GetStaticPaths, GetStaticProps } from 'next';
import { ArrowLeft, ArrowRight, Check, Clock3 } from 'lucide-react';
import SiteLayout from '@/components/site/SiteLayout';
import { blogArticles, type BlogArticle } from '@/data/blog';
import { getAbsoluteUrl, SITE_URL } from '@/lib/seoRoutes';

type BlogArticlePageProps = {
  article: BlogArticle;
};

const BlogArticlePage = ({ article }: BlogArticlePageProps) => {
  return (
    <SiteLayout
      title={`${article.title} | DocMoonlight Blog`}
      description={article.excerpt}
      keywords={[
        article.category,
        'DocMoonlight blog',
        'contrast coverage',
        'imaging center staffing',
        'healthcare operations',
      ]}
      image={article.image}
    >
      <Head>
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: article.title,
              description: article.excerpt,
              image: article.image,
              url: getAbsoluteUrl(`/blog/${article.slug}`),
              datePublished: article.date,
              dateModified: article.date,
              author: {
                '@type': 'Organization',
                name: article.author,
              },
              publisher: {
                '@type': 'Organization',
                name: 'DocMoonlight',
                url: SITE_URL,
                logo: {
                  '@type': 'ImageObject',
                  url: getAbsoluteUrl('/images/logo.svg'),
                },
              },
              mainEntityOfPage: {
                '@type': 'WebPage',
                '@id': getAbsoluteUrl(`/blog/${article.slug}`),
              },
            }),
          }}
        />
      </Head>
      <article className='bg-[#f7f8fa] text-[#111b35]'>
        <section className='relative left-1/2 w-screen -translate-x-1/2 overflow-hidden bg-[#f4f5f7] px-5 py-16 sm:px-6 lg:px-8 lg:py-20'>
          <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_76%_18%,rgba(15,23,42,0.08),transparent_32%),radial-gradient(circle_at_22%_82%,rgba(245,158,11,0.18),transparent_28%)]' />
          <div className='relative mx-auto max-w-5xl'>
            <Link
              href='/blog'
              className='inline-flex items-center gap-2 text-sm font-black text-slate-600 transition hover:text-[#111b35]'
            >
              <ArrowLeft className='h-4 w-4' />
              Back to Blog
            </Link>

            <div className='mt-8'>
              <Link
                href={`/blog?category=${encodeURIComponent(article.category)}`}
                className='inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-[#111b35] shadow-sm transition hover:-translate-y-0.5 hover:border-[#f59e0b]'
              >
                <span className='h-2 w-2 rounded-full bg-[#f59e0b]' />
                {article.category}
              </Link>
            </div>

            <h1 className='mt-7 max-w-5xl text-[2.7rem] font-black leading-[0.98] tracking-tight text-[#111b35] md:text-[4.7rem]'>
              {article.title}
            </h1>

            <p className='mt-6 max-w-3xl text-lg font-semibold leading-8 text-slate-600 md:text-xl'>
              {article.excerpt}
            </p>

            <div className='mt-8 flex flex-wrap items-center gap-4 text-sm font-bold text-slate-500'>
              <span>{article.author}</span>
              <span className='text-slate-300'>.</span>
              <span>{article.date}</span>
              <span className='text-slate-300'>.</span>
              <span className='inline-flex items-center gap-2'>
                <Clock3 className='h-4 w-4' />
                {article.readTime}
              </span>
            </div>
          </div>
        </section>

        <section className='px-5 py-12 sm:px-6 lg:px-8 lg:py-16'>
          <div className='mx-auto max-w-5xl'>
            <div className='overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)]'>
              <div className='relative h-[18rem] bg-slate-200 md:h-[28rem]'>
                <Image
                  src={article.image}
                  alt=''
                  fill
                  sizes='(max-width: 1023px) 100vw, 896px'
                  className='object-cover'
                />
                <div className='absolute inset-0 bg-[linear-gradient(180deg,transparent_45%,rgba(15,23,42,0.44)_100%)]' />
              </div>

              <div className='px-6 py-8 md:px-10 md:py-10'>
                <div className='prose max-w-none'>
                  {article.sections.map((section, index) => (
                    <section key={`${section.heading || 'section'}-${index}`} className='mb-9 last:mb-0'>
                      {section.heading && (
                        <h2 className='mb-4 text-2xl font-black tracking-tight text-[#111b35] md:text-3xl'>
                          {section.heading}
                        </h2>
                      )}
                      {section.body?.map((paragraph) => (
                        <p
                          key={paragraph}
                          className='mb-5 text-lg font-semibold leading-8 text-slate-600 last:mb-0'
                        >
                          {paragraph}
                        </p>
                      ))}
                      {section.bullets && (
                        <ul className='mt-5 space-y-3'>
                          {section.bullets.map((bullet) => (
                            <li
                              key={bullet}
                              className='flex gap-3 text-base font-black leading-7 text-slate-700'
                            >
                              <span className='mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f59e0b] text-white'>
                                <Check className='h-3 w-3' />
                              </span>
                              {bullet}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  ))}
                </div>
              </div>
            </div>

            <aside className='mt-10 rounded-[1.6rem] border border-slate-200 bg-white p-6 shadow-[0_16px_42px_rgba(15,23,42,0.06)] md:p-8'>
              <div className='grid gap-6 md:grid-cols-[1fr_auto] md:items-center'>
                <div>
                  <div className='text-xs font-black uppercase tracking-[0.18em] text-[#f59e0b]'>
                    Need Coverage?
                  </div>
                  <h2 className='mt-3 text-2xl font-black leading-tight text-[#111b35] md:text-3xl'>
                    Build the right contrast coverage model.
                  </h2>
                  <p className='mt-4 max-w-2xl text-sm font-semibold leading-7 text-slate-600 md:text-base'>
                    DocMoonlight helps imaging centers choose onsite, virtual,
                    or hybrid support based on patient needs and operational
                    goals.
                  </p>
                </div>
                <Link
                  href='/contact#contact-form'
                  className='inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-[#111b35] px-7 text-sm font-black text-white shadow-[0_14px_28px_rgba(15,23,42,0.18)] transition hover:-translate-y-1'
                >
                  Contact DocMoonlight
                  <ArrowRight className='h-4 w-4 text-[#f59e0b]' />
                </Link>
              </div>
            </aside>
          </div>
        </section>
      </article>
    </SiteLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => ({
  paths: blogArticles.map((article) => ({
    params: { slug: article.slug },
  })),
  fallback: 'blocking',
});

export const getStaticProps: GetStaticProps<BlogArticlePageProps> = async ({
  params,
}) => {
  const article = blogArticles.find((item) => item.slug === params?.slug);

  if (!article) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      article,
    },
  };
};

export default BlogArticlePage;
